import React, { useEffect, useState } from 'react';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaEye,
  FaSort,
  FaCalendarAlt,
  FaClock,
  FaUserTie,
  FaUsers,
  FaBookOpen,
  FaSearch,
  FaGraduationCap,
  FaSpinner,
  FaInfoCircle,
  FaLayerGroup,
  FaPlay,
  FaCheckCircle,
  FaBan,
  FaExclamationTriangle,
  FaClipboardList,
  FaStar
} from 'react-icons/fa';
import axiosInstance from '../../../../../axios';
import Modal from '../../../../Other/Modal';

const STATUS = {
  ongoing:   { label: 'Active',    dot: 'bg-slate-700', badge: 'bg-slate-100 text-slate-700 border-slate-300',  bar: 'bg-slate-700', accent: 'bg-slate-700' },
  upcoming:  { label: 'Upcoming',  dot: 'bg-slate-400', badge: 'bg-slate-50  text-slate-500 border-slate-200',  bar: 'bg-slate-400', accent: 'bg-slate-400' },
  completed: { label: 'Completed', dot: 'bg-slate-300', badge: 'bg-slate-50  text-slate-400 border-slate-200',  bar: 'bg-slate-300', accent: 'bg-slate-300' },
};

const getStatus = (group) => {
  if (group.status === 'completed') return STATUS.completed;
  if (group.status === 'upcoming')  return STATUS.upcoming;
  return STATUS.ongoing;
};

const StudentGroupBatchList = () => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const handleSubmitFeedback = async () => {
    if (!selectedGroup) return;
    const token = JSON.parse(localStorage.getItem("token"));
    setSubmittingFeedback(true);
    try {
      const response = await axiosInstance.post(
        `/student/submit-group-feedback`,
        {
          groupId: selectedGroup.groupId,
          rating: feedbackRating,
          comments: feedbackComments,
        },
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        }
      );
      if (response.data.error) {
        alert(response.data.message || "Failed to submit feedback.");
      } else {
        alert("Thank you for your feedback!");
        setShowFeedbackModal(false);
        setFeedbackComments('');
        setFeedbackRating(5);
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting feedback.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const fetchGroups = async () => {
    setLoading(true);
    setMessage('');
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axiosInstance.get(`/student/group-batch-list`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.data.error) {
        setMessage(response.data.message || 'Failed to fetch data.');
      } else {
        const data = response.data.data || [];
        const statusOrder = {
          'ongoing': 1,
          'upcoming': 2,
          'completed': 3
        };
        const sortedData = [...data].sort((a, b) => {
          const statusA = (a.status || '').toLowerCase();
          const statusB = (b.status || '').toLowerCase();
          const orderA = statusOrder[statusA] || 99;
          const orderB = statusOrder[statusB] || 99;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const idA = parseInt(a.groupId || 0, 10);
          const idB = parseInt(b.groupId || 0, 10);
          return idB - idA;
        });
        setGroups(sortedData);
        setFilteredGroups(sortedData);
      }
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetail = async (groupId) => {
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axiosInstance.get(`/admin/group-batch/${groupId}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.data.error) {
        setSelectedGroup(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  useEffect(() => {
    const filtered = groups.filter(group =>
      group.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.batchName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGroups(filtered);
    setCurrentPage(1);
  }, [searchTerm, groups]);

  useEffect(() => {
    if (sortConfig.key !== '') {
      const sorted = [...filteredGroups].sort((a, b) => {
        let aVal = (sortConfig.key === 'instructors' || sortConfig.key === 'students')
          ? a[sortConfig.key]?.length || 0
          : a[sortConfig.key];
        let bVal = (sortConfig.key === 'instructors' || sortConfig.key === 'students')
          ? b[sortConfig.key]?.length || 0
          : b[sortConfig.key];
        if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      setFilteredGroups(sorted);
    }
  }, [sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const indexOfLastItem  = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems     = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages       = Math.ceil(filteredGroups.length / itemsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not Scheduled';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getBatchProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    try {
      const start = new Date(startDate).getTime();
      const end   = new Date(endDate).getTime();
      const now   = Date.now();
      if (now < start) return 0;
      if (now > end)   return 100;
      return Math.round(((now - start) / (end - start)) * 100);
    } catch { return 0; }
  };

  const totalBatchesCount    = groups.length;
  const activeBatchesCount   = groups.filter(g => g.status === 'ongoing' || !g.status).length;
  const upcomingBatchesCount = groups.filter(g => g.status === 'upcoming').length;

  const SortButton = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className={`px-3.5 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-all ${
        sortConfig.key === field
          ? 'bg-slate-700 text-white shadow-sm'
          : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {label}
      {sortConfig.key === field
        ? (sortConfig.direction === 'asc' ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />)
        : <FaSort className="text-xs opacity-50" />}
    </button>
  );

  return (
    <div className="p-5 bg-white min-h-screen">

      {/* Page Header */}
      <div className="mb-6 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        {/* <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} /> */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <FaGraduationCap className="text-white text-xl" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight">My Learning Cohorts</h1>
            </div>
            <p className="text-slate-300 text-base pl-0.5">
              View your batch schedules, classmates, and instructors.
            </p>
          </div>

          {/* Stat Pills */}
          <div className="flex gap-2.5 flex-wrap">
            {[
              { label: 'Total',    value: totalBatchesCount,    color: 'bg-white/10 text-white' },
              { label: 'Active',   value: activeBatchesCount,   color: 'bg-white/10 text-white' },
              { label: 'Upcoming', value: upcomingBatchesCount, color: 'bg-white/10 text-white' },
            ].map(stat => (
              <div key={stat.label} className={`${stat.color} px-5 py-2.5 rounded-xl text-center border border-white/10`}>
                <span className="block text-xs font-semibold uppercase tracking-wider opacity-70">{stat.label}</span>
                <span className="text-2xl font-bold">{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative w-full max-w-xs">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
          <input
            type="text"
            placeholder="Search course or batch..."
            className="pl-10 pr-3 py-2.5 w-full bg-white border border-slate-200 rounded-xl shadow-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-slate-800 text-sm outline-none placeholder:text-slate-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-md">
          <span className="text-xs text-slate-400 font-semibold uppercase px-2">Sort by</span>
          <SortButton field="courseName" label="Course" />
          <SortButton field="batchName"  label="Batch"  />
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-200 shadow-lg">
          <FaSpinner className="animate-spin text-4xl text-slate-500 mb-4" />
          <p className="text-slate-500 text-base font-medium">Loading your cohorts...</p>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-200 shadow-lg text-center px-6">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 text-2xl mb-4">
            <FaBookOpen />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">No Groups Found</h3>
          <p className="text-slate-500 max-w-sm mt-1.5 text-sm">
            No learning cohorts match your search criteria or active courses.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentItems.map((group, idx) => {
              const status   = getStatus(group);
              const progress = getBatchProgress(group.startDate, group.endDate);

              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-200 border border-slate-200 overflow-hidden flex flex-col"
                >
                  {/* Status Accent Bar */}
                  <div className={`h-1 w-full ${status.accent}`} />

                  <div className="p-5 flex-1 flex flex-col">
                    {/* Header Row */}
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-slate-400 tracking-wide uppercase">
                        #{group.groupId}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${status.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        {status.label}
                      </span>
                    </div>

                    {/* Course Title */}
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                        <FaBookOpen className="text-slate-500 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-800 text-base leading-snug line-clamp-2">
                          {group.courseName}
                        </h3>
                        <p className="text-sm text-slate-400 mt-0.5">
                          <FaLayerGroup className="inline mr-1 text-xs" />
                          {group.batchName}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="my-3">
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="text-xs text-slate-400 font-medium">Batch Progress</span>
                        <span className="text-xs font-semibold text-slate-600">{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${status.bar} rounded-full transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="border-t border-slate-100 my-3" />

                    {/* Schedule Info */}
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2.5 text-sm text-slate-600">
                        <FaCalendarAlt className="text-slate-300 shrink-0" />
                        <span>
                          {formatDate(group.startDate)}
                          <span className="text-slate-300 mx-1.5">—</span>
                          {formatDate(group.endDate)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm text-slate-600">
                        <FaClock className="text-slate-300 shrink-0" />
                        <span>
                          {group.startTime && group.endTime
                            ? `${group.startTime} – ${group.endTime}`
                            : 'Time TBD'}
                          {group.daysType && (
                            <span className="ml-1.5 text-slate-400">· {group.daysType}</span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex gap-4">
                        <span className="flex items-center gap-1.5 text-sm text-slate-500" title="Instructors">
                          <FaUserTie className="text-slate-300" />
                          {group.instructors?.length || 0}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-500" title="Students">
                          <FaUsers className="text-slate-300" />
                          {group.students?.length || 0}
                        </span>
                      </div>

                      <button
                        onClick={() => {
                          setSelectedGroup(group);
                          setActiveTab('overview');
                          setViewModal(true);
                          fetchGroupDetail(group.groupId);
                        }}
                        className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all active:scale-95 cursor-pointer"
                      >
                        <FaEye className="text-xs" />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-5 flex justify-between items-center bg-white px-5 py-3.5 rounded-xl border border-slate-200 shadow-md">
              <span className="text-sm text-slate-500">
                Showing{' '}
                <span className="font-semibold text-slate-700">{indexOfFirstItem + 1}–{Math.min(indexOfLastItem, filteredGroups.length)}</span>
                {' '}of{' '}
                <span className="font-semibold text-slate-700">{filteredGroups.length}</span>
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronLeft className="text-xs" />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-9 h-9 text-sm font-semibold rounded-lg transition-all ${
                      currentPage === i + 1
                        ? 'bg-slate-800 text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Modal isOpen={viewModal} onClose={() => setViewModal(false)}>
        {selectedGroup && (() => {
          const status   = getStatus(selectedGroup);
          const progress = getBatchProgress(selectedGroup.startDate, selectedGroup.endDate);
          return (
            <div className="flex flex-col max-h-[85vh] overflow-hidden">

              {/* Modal Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 rounded-t-2xl relative overflow-hidden">
                <div className="absolute right-0 top-0 w-36 h-36 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-white/15 text-white border border-white/20`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white leading-tight">{selectedGroup.courseName}</h2>
                  <p className="text-slate-300 text-base mt-1">{selectedGroup.batchName}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="sticky top-0 z-20 flex border-b border-slate-200 bg-white px-2 overflow-x-auto scrollbar-none shadow-xs">
                {[
                  { id: 'overview',          label: 'Overview',                                         icon: <FaInfoCircle /> },
                  { id: 'syllabus_progress', label: 'Syllabus Progress Checklist',                     icon: <FaClipboardList /> },
                  { id: 'instructors',       label: `Instructors (${selectedGroup.instructors?.length || 0})`, icon: <FaUserTie /> },
                  { id: 'classmates',        label: `Classmates (${selectedGroup.students?.length || 0})`,     icon: <FaUsers />   },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3.5 px-4 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 outline-none whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-slate-700 text-slate-800'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-5 overflow-y-auto flex-1 bg-slate-100 ">

                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    {/* Progress */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-md">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-slate-600">Batch Progress</span>
                        <span className="text-sm font-bold text-slate-700">{progress}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${status.bar} rounded-full transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-xs text-slate-400">{formatDate(selectedGroup.startDate)}</span>
                        <span className="text-xs text-slate-400">{formatDate(selectedGroup.endDate)}</span>
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Start Date',  value: formatDate(selectedGroup.startDate), icon: <FaCalendarAlt /> },
                        { label: 'End Date',    value: formatDate(selectedGroup.endDate),   icon: <FaCalendarAlt /> },
                        {
                          label: 'Class Time',
                          value: selectedGroup.startTime && selectedGroup.endTime
                            ? `${selectedGroup.startTime} – ${selectedGroup.endTime}`
                            : 'Not Defined',
                          icon: <FaClock />
                        },
                        { label: 'Schedule', value: selectedGroup.daysType || 'Weekly', icon: <FaCalendarAlt /> },
                      ].map((item, i) => (
                        <div key={i} className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-md">
                          <div className="flex items-center gap-1.5 text-slate-400 mb-1.5">
                            <span className="text-xs">{item.icon}</span>
                            <span className="uppercase tracking-wide font-semibold text-xs">{item.label}</span>
                          </div>
                          <span className="text-slate-800 font-semibold text-base">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'syllabus_progress' && (
                  <div className="space-y-4">
                    {!selectedGroup.sessions ? (
                      <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <FaSpinner className="animate-spin text-3xl text-slate-500 mb-3" />
                        <p className="text-slate-500 text-sm font-medium">Loading syllabus checklist...</p>
                      </div>
                    ) : selectedGroup.sessions.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-6 text-slate-400">
                        No sessions or syllabus checklist available for this course.
                      </div>
                    ) : (
                      (() => {
                        const totalSessions = selectedGroup.sessions.length;
                        const completedCount = selectedGroup.sessions.filter(s => s.status === 'completed').length;
                        const ongoingCount = selectedGroup.sessions.filter(s => s.status === 'ongoing').length;
                        const skippedCount = selectedGroup.sessions.filter(s => s.status === 'skipped').length;
                        const pendingCount = selectedGroup.sessions.filter(s => s.status === 'pending').length;
                        const completionPct = totalSessions > 0 ? Math.round((completedCount / totalSessions) * 100) : 0;
                        const showFeedbackBtn = totalSessions > 0 && (completedCount / totalSessions) >= 0.90;
                        const finishedPercent = totalSessions > 0 ? (completedCount / totalSessions) : 0;

                        return (
                          <>
                            {/* Progress Stats Summary Card */}
                            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-md space-y-4">
                              <div className="flex justify-between items-center">
                                <div>
                                  <h4 className="text-base font-black text-slate-800 uppercase tracking-wider">Syllabus Progress</h4>
                                  <p className="text-xs text-slate-400 font-bold mt-0.5">
                                    {completedCount} of {totalSessions} sessions completed
                                  </p>
                                </div>
                                <span className="text-2xl font-black text-slate-700">{completionPct}%</span>
                              </div>

                              <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                                <div
                                  className="h-full bg-gradient-to-r from-slate-750 to-slate-800 rounded-full transition-all duration-1000"
                                  style={{ width: `${completionPct}%` }}
                                />
                              </div>

                              {/* Mini Legend / Count Stats */}
                              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-slate-100 text-center">
                                <div className="bg-slate-50 p-2 rounded-lg border border-slate-150">
                                  <span className="block text-[10px] uppercase font-bold text-slate-400">Pending</span>
                                  <span className="text-sm font-black text-slate-600">{pendingCount}</span>
                                </div>
                                <div className="bg-blue-50/50 p-2 rounded-lg border border-blue-100">
                                  <span className="block text-[10px] uppercase font-bold text-blue-500">Ongoing</span>
                                  <span className="text-sm font-black text-blue-600">{ongoingCount}</span>
                                </div>
                                <div className="bg-emerald-50/50 p-2 rounded-lg border border-emerald-100">
                                  <span className="block text-[10px] uppercase font-bold text-emerald-500">Completed</span>
                                  <span className="text-sm font-black text-emerald-600">{completedCount}</span>
                                </div>
                                <div className="bg-rose-50/50 p-2 rounded-lg border border-rose-100">
                                  <span className="block text-[10px] uppercase font-bold text-rose-500">Skipped</span>
                                  <span className="text-sm font-black text-rose-600">{skippedCount}</span>
                                </div>
                              </div>
                            </div>

                            {/* Sessions Checklist */}
                            <div className="space-y-3">
                              {selectedGroup.sessions.map((session, index) => {
                                let statusBg = "bg-slate-50 text-slate-500 border-slate-200/60";
                                let statusIcon = <FaClock className="text-slate-400 w-3.5 h-3.5" />;
                                if (session.status === 'ongoing') {
                                  statusBg = "bg-blue-50 text-blue-700 border-blue-200/50";
                                  statusIcon = <FaPlay className="text-blue-500 w-3 h-3 animate-pulse" />;
                                } else if (session.status === 'completed') {
                                  statusBg = "bg-emerald-50 text-emerald-700 border-emerald-200/50";
                                  statusIcon = <FaCheckCircle className="text-emerald-500 w-3.5 h-3.5" />;
                                } else if (session.status === 'skipped') {
                                  statusBg = "bg-rose-50 text-rose-700 border-rose-200/50";
                                  statusIcon = <FaBan className="text-rose-500 w-3.5 h-3.5" />;
                                }

                                return (
                                  <div
                                    key={session.courseDetailId}
                                    className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm transition-all hover:shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-3"
                                  >
                                    <div className="flex-1 min-w-0 space-y-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200/50">
                                          Session {index + 1}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border ${statusBg}`}>
                                          {statusIcon}
                                          <span>{session.status}</span>
                                        </span>
                                      </div>

                                      <h4 className="text-sm font-bold text-slate-800">
                                        {session.mainTitle || "Topic Title"}
                                      </h4>
                                      
                                      {session.sectionName && (
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                          Section: {session.sectionName}
                                        </p>
                                      )}

                                      {session.sectionDescription && (
                                        <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                                          {session.sectionDescription}
                                        </p>
                                      )}

                                      {session.status === 'skipped' && session.skipReason && (
                                        <div className="mt-2 p-2.5 rounded-lg bg-rose-50/50 border border-rose-100/30 text-xs text-rose-700 flex items-start gap-2">
                                          <FaExclamationTriangle className="mt-0.5 text-rose-500 flex-shrink-0" />
                                          <div>
                                            <span className="font-bold">Skip Reason:</span> {session.skipReason}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    <div className="mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-slate-100 flex flex-col items-start md:items-end justify-center shrink-0">
                                      <span className="text-[10px] text-slate-400 font-medium">Covered By</span>
                                      <span className="text-xs font-semibold text-slate-700">
                                        {session.instructorName || "Primary Instructor"}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Feedback Button Section */}
                            {showFeedbackBtn && (
                              <div className="mt-6 pt-5 border-t border-slate-200 flex flex-col items-center gap-3">
                                <div className="text-center">
                                  <h5 className="text-sm font-black text-slate-700">Congratulations on completing {completionPct}% of your syllabus!</h5>
                                  <p className="text-xs text-slate-400">Please take a moment to share your learning experience with us.</p>
                                </div>
                                <button
                                  onClick={() => setShowFeedbackModal(true)}
                                  className="px-6 py-2.5 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-md active:scale-95 cursor-pointer flex items-center gap-2"
                                >
                                  <FaClipboardList className="text-sm" /> Give Program Feedback
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()
                    )}
                  </div>
                )}

                {activeTab === 'instructors' && (
                  <div className="space-y-3">
                    {!selectedGroup.instructors?.length ? (
                      <div className="text-center py-12 text-slate-400 text-base">No instructors assigned yet.</div>
                    ) : (
                      selectedGroup.instructors.map((inst) => (
                        <div key={inst?.id} className="flex items-center gap-3.5 bg-white border border-slate-200 p-4 rounded-xl shadow-md">
                          <div className="w-11 h-11 rounded-full bg-slate-700 text-white flex items-center justify-center font-semibold text-sm shrink-0">
                            {getInitials(inst?.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 text-base truncate">{inst?.name}</h4>
                            <p className="text-sm text-slate-400 mt-0.5">
                              {inst?.phoneNumber || 'Instructor'}
                            </p>
                          </div>
                          <span className="text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-1 rounded-full">
                            Instructor
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'classmates' && (
                  <div className="space-y-2.5">
                    {!selectedGroup.students?.length ? (
                      <div className="text-center py-12 text-slate-400 text-base">No classmates registered in this batch.</div>
                    ) : (
                      selectedGroup.students.map((std) => (
                        <div key={std?.id} className="flex items-center gap-3.5 bg-white border border-slate-200 p-3.5 rounded-xl shadow-md hover:shadow-lg hover:border-slate-300 transition-all">
                          <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-semibold text-sm shrink-0">
                            {getInitials(std?.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-800 text-base truncate">{std?.name}</h4>
                            <p className="text-sm text-slate-400 mt-0.5">
                              {std?.phoneNumber || 'Details Restricted'}
                            </p>
                          </div>
                          <span className="text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-1 rounded-full">
                            Student
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 bg-white border-t border-slate-200 flex justify-end rounded-b-2xl">
                <button
                  onClick={() => setViewModal(false)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg text-sm font-medium transition-all active:scale-95 cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Feedback Submission Modal */}
      <Modal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)}>
        <div className="p-6 space-y-4">
          <div className="text-center">
            <h3 className="text-xl font-bold text-slate-800">Submit Batch Feedback</h3>
            <p className="text-xs text-slate-450 mt-1">Your feedback helps us continuously improve our program delivery.</p>
          </div>

          {/* Star Rating Selector */}
          <div className="space-y-1.5 text-center">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">Program Rating</label>
            <div className="flex justify-center gap-1.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setFeedbackRating(star)}
                  className="p-1 transition-all transform hover:scale-110 active:scale-95"
                >
                  <FaStar
                    className={`w-7 h-7 cursor-pointer transition-colors duration-150 ${
                      feedbackRating >= star ? "text-amber-400 fill-amber-400" : "text-slate-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Comments Textarea */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">Comments / Suggestions</label>
            <textarea
              value={feedbackComments}
              onChange={(e) => setFeedbackComments(e.target.value)}
              placeholder="What did you like about the program? How can we make it even better?"
              rows={4}
              className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
            <button
              onClick={() => setShowFeedbackModal(false)}
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-650 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback}
              className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50"
            >
              {submittingFeedback ? "Submitting..." : "Submit Feedback"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentGroupBatchList;
